import type { SharedAccessPermissions } from '../../shared/api/models'
export type { SharedAccessPermissions } from '../../shared/api/models'

export function getDefaultSharedAccessPermissions(): SharedAccessPermissions {
  return {
    accounts: { view: false, create: false, edit: false, reconcile: false, transfer: false },
    expenses: { view: true, create: false, edit: false, delete: false },
    incomes: { view: true, create: false, edit: false, delete: false },
    analytics: { view: true },
    categories: { view: true, manage: false },
    limits: { view: true, manage: false },
    planning: { view: true, manage: false },
    debts: { view: true, create: false, edit: false, delete: false },
    goals: { view: true, create: false, edit: false, delete: false },
    recurringPayments: { view: true, manage: false },
    notifications: { viewOwn: true, manageOwn: false },
    sharedAccess: { manage: false },
    audit: { view: false },
  }
}

function asRecord(input: unknown): Record<string, unknown> {
  return input && typeof input === 'object' && !Array.isArray(input) ? (input as Record<string, unknown>) : {}
}

function readBoolSection<T extends Record<string, boolean>>(base: T, input: unknown): T {
  const raw = asRecord(input)
  const out = { ...base }
  for (const key of Object.keys(base) as Array<keyof T>) {
    out[key] = (raw[key as string] === true) as T[keyof T]
  }
  return out
}

export function normalizeSharedAccessPermissions(input: unknown): SharedAccessPermissions {
  const base = getDefaultSharedAccessPermissions()
  const raw = asRecord(input)
  const next: SharedAccessPermissions = {
    accounts: readBoolSection(base.accounts, raw.accounts),
    expenses: readBoolSection(base.expenses, raw.expenses),
    incomes: readBoolSection(base.incomes, raw.incomes),
    analytics: readBoolSection(base.analytics, raw.analytics),
    categories: readBoolSection(base.categories, raw.categories),
    limits: readBoolSection(base.limits, raw.limits),
    planning: readBoolSection(base.planning, raw.planning),
    debts: readBoolSection(base.debts, raw.debts),
    goals: readBoolSection(base.goals, raw.goals),
    recurringPayments: readBoolSection(base.recurringPayments, raw.recurringPayments),
    notifications: readBoolSection(base.notifications, raw.notifications),
    sharedAccess: readBoolSection(base.sharedAccess, raw.sharedAccess),
    audit: readBoolSection(base.audit, raw.audit),
  }

  for (const resource of ['expenses', 'incomes', 'debts', 'goals'] as const) {
    if (!next[resource].view) next[resource] = { view: false, create: false, edit: false, delete: false }
  }
  for (const resource of ['categories', 'limits', 'planning'] as const) if (!next[resource].view) next[resource].manage = false
  if (!next.notifications.viewOwn) next.notifications.manageOwn = false
  if (!next.accounts.view) next.accounts = { view: false, create: false, edit: false, reconcile: false, transfer: false }
  next.recurringPayments = { ...next.planning }

  return next
}

export function can(p: SharedAccessPermissions | null | undefined, section: keyof SharedAccessPermissions, key: string): boolean {
  if (!p) return false
  const sec = p[section] as Record<string, boolean> | undefined
  if (!sec) return false
  return sec[key] === true
}
